<script setup>
import NextButton from 'dashboard/components-next/button/Button.vue';

defineProps({
  name: { type: String, default: '' },
  status: { type: String, default: 'draft' },
  dirty: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  hasErrors: { type: Boolean, default: false },
  triggerLabel: { type: String, default: '' },
  inboxesSummary: { type: String, default: '' },
  triggerType: { type: String, default: '' },
});

const emit = defineEmits([
  'back',
  'updateName',
  'save',
  'validate',
  'activate',
  'deactivate',
  'showJson',
  'insertExample',
  'fitView',
  'settings',
]);
</script>

<template>
  <div class="flex flex-col gap-1 px-3 py-2 border-b border-n-weak bg-n-solid-1">
    <div class="flex items-center gap-2">
      <NextButton icon="i-lucide-arrow-left" slate faded xs @click="emit('back')" />
      <input
        :value="name"
        type="text"
        class="!mb-0 !w-56 text-sm font-medium"
        @change="emit('updateName', $event.target.value)"
      />
      <span
        class="px-2 py-0.5 text-xs rounded-md"
        :class="status === 'active' ? 'bg-n-teal-3 text-n-teal-11' : status === 'inactive' ? 'bg-n-amber-3 text-n-amber-11' : 'bg-n-slate-3 text-n-slate-11'"
      >
        {{ $t(`FLOWS.STATUS.${status.toUpperCase()}`) }}
      </span>

      <span class="text-xs ltr:ml-2 rtl:mr-2" :class="hasErrors ? 'text-n-ruby-11' : dirty ? 'text-n-amber-11' : 'text-n-slate-10'">
        <template v-if="saving">{{ $t('FLOWS.BUILDER.TOOLBAR.SAVING') }}</template>
        <template v-else-if="hasErrors">{{ $t('FLOWS.BUILDER.TOOLBAR.HAS_ERRORS') }}</template>
        <template v-else-if="dirty">{{ $t('FLOWS.BUILDER.TOOLBAR.UNSAVED') }}</template>
        <template v-else>{{ $t('FLOWS.BUILDER.TOOLBAR.SAVED') }}</template>
      </span>

      <div class="flex items-center gap-1.5 ltr:ml-auto rtl:mr-auto">
        <NextButton v-tooltip.bottom="$t('FLOWS.BUILDER.TOOLBAR.FIT_VIEW')" icon="i-lucide-scan" slate faded xs @click="emit('fitView')" />
        <NextButton icon="i-lucide-settings-2" :label="$t('FLOWS.CARD.SETTINGS')" slate faded xs @click="emit('settings')" />
        <NextButton :label="$t('FLOWS.BUILDER.TOOLBAR.INSERT_EXAMPLE')" slate faded xs @click="emit('insertExample')" />
        <NextButton :label="$t('FLOWS.BUILDER.TOOLBAR.SHOW_JSON')" slate faded xs @click="emit('showJson')" />
        <NextButton :label="$t('FLOWS.BUILDER.TOOLBAR.VALIDATE')" slate xs @click="emit('validate')" />
        <NextButton :label="$t('FLOWS.FORM.SAVE')" xs :is-loading="saving" @click="emit('save')" />
        <NextButton v-if="status !== 'active'" :label="$t('FLOWS.BUILDER.TOOLBAR.ACTIVATE')" teal xs @click="emit('activate')" />
        <NextButton v-else :label="$t('FLOWS.BUILDER.TOOLBAR.DEACTIVATE')" amber xs @click="emit('deactivate')" />
      </div>
    </div>
    <div class="flex items-center gap-3 ltr:pl-9 rtl:pr-9 text-xs text-n-slate-11">
      <span v-if="triggerLabel" class="flex items-center gap-1">
        {{ $t('FLOWS.CARD.TRIGGER') }}: <span class="text-n-slate-12">{{ triggerLabel }}</span>
      </span>
      <span v-if="inboxesSummary" class="flex items-center gap-1">
        {{ $t('FLOWS.CARD.INBOXES') }}: <span class="text-n-slate-12">{{ inboxesSummary }}</span>
      </span>
      <span v-if="triggerType === 'manual'" class="text-n-amber-11">
        {{ $t('FLOWS.FORM.TRIGGER.MANUAL_HINT') }}
      </span>
    </div>
  </div>
</template>
